/**
 * Roaming-legendary OVERLAY (M-iter, owner direction: "make sure they show up").
 *
 * Legendaries are no longer ordinary low-level entries in the biome tables. Instead
 * a SINGLE legendary roams the region at a time, drifting between biomes, and may
 * surface on a qualifying expedition as a RARE, HIGH-LEVEL special wild — on top of
 * the frozen biome encounter tables (core/encounter is untouched; this never changes
 * the `EncounterEntry` shape or the daily route generation).
 *
 * The triple contract (CLAUDE.md) shapes every number here:
 *   - RARE per outing: the per-expedition chance is small, so a legendary is a hush,
 *     never a routine catch.
 *   - RELIABLE over time: a GENTLE PITY raises the odds the longer it has been since
 *     a legendary surfaced, ramping to a near-certainty within a bounded window — so
 *     "a fled legendary always comes back around," no missable legendaries, no FOMO.
 *   - HIGH LEVEL by strength: the level is driven by the species' base-stat total, so
 *     box legends / Mewtwo land high (~62–70) and the birds/beasts/Regis land ~50–58.
 *     ALWAYS strictly above level 50.
 *
 * Catch stays HARD via the unchanged capture-rate formula (core/catch + the species
 * capture rate, ~3 for most legendaries) — this module never eases catching.
 *
 * Pure + deterministic: every roll flows through the injected world seed + date, so
 * the same (seed, date, node, dataset) always yields the same legendary outcome.
 * No save fields are needed — the roaming target, the drift biome, and the pity ramp
 * are all derived from `hash(worldSeed + period)` and the in-game day index, so this
 * adds nothing to the frozen Save contract.
 */
import type { BiomeData, BiomeKind, GeneratedEncounter, SpeciesData } from '@/shared';
import { hash, makeRng } from './rng.js';

// ── Level by strength ─────────────────────────────────────────────────────────

/** The base-stat total all legendary leveling/centering keys off. */
export function baseStatTotal(species: SpeciesData): number {
  const b = species.baseStats;
  return b.hp + b.atk + b.def + b.spatk + b.spdef + b.spe;
}

/** Lowest level a roaming legendary may ever spawn at — ALWAYS strictly above 50. */
export const LEGENDARY_MIN_LEVEL = 51;
/** Highest level a roaming legendary spawns at (box legends / Mewtwo territory). */
export const LEGENDARY_MAX_LEVEL = 70;

/**
 * BST centred so the famous ~580-BST tier (the birds, the Johto beasts, the Regis)
 * lands near the floor and the ~680–720-BST box legends / Mewtwo land near the top.
 */
const LEGENDARY_BST_PIVOT = 580;
/** BST-per-level divisor. ~8 BST ⇒ +1 level: 580→50base, 680→~62, 720→~67. */
const LEGENDARY_BST_PER_LEVEL = 8;

/**
 * The high level a legendary spawns at, scaled by its base-stat total:
 *   level = clamp(round(50 + (BST − 580) / 8), 51, 70)
 * so ~580-BST legendaries land ~51–58 and ~680–720-BST box legends / Mewtwo land
 * ~62–70. ALWAYS strictly above 50 (the 50 anchor is bumped to the 51 floor).
 */
export function legendaryLevel(species: SpeciesData): number {
  const bst = baseStatTotal(species);
  const raw = Math.round(50 + (bst - LEGENDARY_BST_PIVOT) / LEGENDARY_BST_PER_LEVEL);
  return Math.min(LEGENDARY_MAX_LEVEL, Math.max(LEGENDARY_MIN_LEVEL, raw));
}

// ── Roaming target + drift ────────────────────────────────────────────────────

/** A legendary species is one the dataset flags as legendary OR mythical. */
export function isLegendarySpecies(species: SpeciesData): boolean {
  return species.isLegendary === true || species.isMythical === true;
}

/** Filter a dataset to its legendary/mythical species, sorted by dex for stability. */
export function legendaryPool(species: readonly SpeciesData[]): readonly SpeciesData[] {
  return species
    .filter(isLegendarySpecies)
    .slice()
    .sort((a, b) => a.dexNo - b.dexNo);
}

/**
 * How many in-game days a single legendary roams before the next one takes over.
 * Within one period the pity ramps to a near-certainty, so each period is GUARANTEED
 * to surface its legendary at least once (the "always comes back around" promise),
 * then a fresh legendary rotates in.
 */
export const ROAM_PERIOD_DAYS = 7;

/**
 * Parse a `YYYY-MM-DD` in-game date into a whole UTC day number (days since the
 * epoch). Self-contained + pure so core stays decoupled from session/date. NaN-safe:
 * a malformed date floors to day 0 (a calm fallback, never a crash).
 */
export function dayNumber(inGameDate: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(inGameDate.trim());
  if (!m) return 0;
  const d = new Date(0);
  d.setUTCFullYear(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setUTCHours(0, 0, 0, 0);
  const ms = d.getTime();
  if (!Number.isFinite(ms)) return 0;
  return Math.floor(ms / 86_400_000);
}

/** The roaming period index for an in-game day (one new legendary per period). */
export function roamPeriod(inGameDate: string): number {
  return Math.floor(dayNumber(inGameDate) / ROAM_PERIOD_DAYS);
}

/** 0-based day offset within the current roaming period (0 … ROAM_PERIOD_DAYS-1). */
export function dayWithinPeriod(inGameDate: string): number {
  const day = dayNumber(inGameDate);
  return ((day % ROAM_PERIOD_DAYS) + ROAM_PERIOD_DAYS) % ROAM_PERIOD_DAYS;
}

/** The legendary roaming this period, chosen deterministically from hash(seed+period). */
export function roamingLegendary(
  worldSeed: number,
  inGameDate: string,
  pool: readonly SpeciesData[],
): SpeciesData | null {
  if (pool.length === 0) return null;
  const period = roamPeriod(inGameDate);
  const rng = makeRng(hash(worldSeed, `legendary-target|${period}`));
  return pool[rng.int(pool.length)] ?? null;
}

/**
 * Which biome KIND each legendary drifts toward, by species id. Drives the
 * "qualifying expedition" gate (it only surfaces where it is currently roaming) and
 * the lore rumor's hint. Anything not listed falls back to a deterministic biome so
 * every legendary always has somewhere to roam.
 */
const LEGENDARY_HOME_KIND: Readonly<Record<string, BiomeKind>> = {
  // Gen 1 birds + Mewtwo / Mew
  articuno: 'cave',
  zapdos: 'mountain',
  moltres: 'mountain',
  mewtwo: 'ruins',
  mew: 'forest',
  // Gen 2 beasts + tower duo + Lugia/Ho-Oh + Celebi
  raikou: 'grassland',
  entei: 'mountain',
  suicune: 'lake',
  lugia: 'sea',
  'ho-oh': 'mountain',
  celebi: 'forest',
  // Gen 3 Regis + Eon duo + weather trio + Jirachi/Deoxys
  regirock: 'cave',
  regice: 'lake',
  registeel: 'ruins',
  latias: 'meadow',
  latios: 'forest',
  kyogre: 'sea',
  groudon: 'mountain',
  rayquaza: 'mountain',
  jirachi: 'ruins',
  deoxys: 'ruins',
};

/** All biome kinds the region offers (mirrors the BiomeKind union, sans 'town'). */
const ROAMABLE_KINDS: readonly BiomeKind[] = [
  'meadow',
  'forest',
  'cave',
  'mountain',
  'lake',
  'sea',
  'beach',
  'marsh',
  'grassland',
  'ruins',
];

/**
 * The biome KIND the active legendary is drifting toward on a given day. Authored
 * home kind when known; otherwise a deterministic kind from hash(seed+period). The
 * drift shifts each period (a fresh legendary), so the legendary "moves around."
 */
export function roamingBiomeKind(
  worldSeed: number,
  inGameDate: string,
  species: SpeciesData,
): BiomeKind {
  const authored = LEGENDARY_HOME_KIND[species.id];
  if (authored) return authored;
  const period = roamPeriod(inGameDate);
  const rng = makeRng(hash(worldSeed, `legendary-biome|${species.id}|${period}`));
  return ROAMABLE_KINDS[rng.int(ROAMABLE_KINDS.length)] ?? 'meadow';
}

// ── Rare-but-reliable spawn chance (gentle pity) ───────────────────────────────

/** Base per-qualifying-expedition chance on day 0 of a period — RARE. */
export const LEGENDARY_BASE_CHANCE = 0.18;
/**
 * Per-day pity lift WITHIN a period. By the last day of the period the chance
 * reaches certainty, so each roaming legendary is GUARANTEED to surface at least once
 * before the next rotates in (no missable legendaries). Tuned so a 7-day period ramps
 * 0.18 → 1.0 across days 0…6.
 */
export const LEGENDARY_PITY_PER_DAY =
  (1 - LEGENDARY_BASE_CHANCE) / Math.max(1, ROAM_PERIOD_DAYS - 1);

/**
 * The legendary spawn chance for a qualifying expedition on a given in-game day:
 * the small base lifted by gentle per-day pity, clamped to [base, 1]. Rare early in
 * the period, certain by its end.
 */
export function legendarySpawnChance(inGameDate: string): number {
  const lift = LEGENDARY_PITY_PER_DAY * dayWithinPeriod(inGameDate);
  return Math.min(1, LEGENDARY_BASE_CHANCE + lift);
}

// ── Overlay result ─────────────────────────────────────────────────────────────

/** The outcome of rolling the legendary overlay for one expedition. */
export interface LegendaryRoll {
  /** The legendary roaming this period (for a rumor even when it doesn't spawn here). */
  readonly roaming: SpeciesData | null;
  /** The biome KIND it is drifting toward (the qualifying biome / rumor hint). */
  readonly roamingKind: BiomeKind | null;
  /** Whether THIS expedition is a qualifying one (the player's biome matches). */
  readonly qualifies: boolean;
  /**
   * The high-level legendary encounter to place as a rare special wild, or null when
   * it did not surface this outing (rare per outing — try again, it comes back around).
   */
  readonly encounter: GeneratedEncounter | null;
}

/** Inputs for {@link rollLegendaryOverlay} (the node's biome + the dataset). */
export interface LegendaryRollArgs {
  readonly worldSeed: number;
  readonly inGameDate: string;
  /** A stable id for the chosen destination (anchors the per-expedition roll). */
  readonly nodeId: string;
  /** The biome the expedition is in (its `kind` gates "qualifying"). */
  readonly biome: BiomeData;
  /** The full species dataset — legendaries are filtered out of it by flag. */
  readonly species: readonly SpeciesData[];
}

/**
 * Roll the roaming-legendary overlay for one expedition. Deterministic from
 * `hash(worldSeed + date + nodeId)`. Returns the roaming legendary + its drift biome
 * (always, for a rumor), and — only on a QUALIFYING expedition that passes the
 * pity-boosted chance — a single HIGH-LEVEL special encounter to place atop the
 * normal route. Never touches the biome tables or core/encounter.
 */
export function rollLegendaryOverlay(args: LegendaryRollArgs): LegendaryRoll {
  const { worldSeed, inGameDate, nodeId, biome, species } = args;
  const pool = legendaryPool(species);
  const roaming = roamingLegendary(worldSeed, inGameDate, pool);

  if (!roaming) {
    return { roaming: null, roamingKind: null, qualifies: false, encounter: null };
  }

  const roamingKind = roamingBiomeKind(worldSeed, inGameDate, roaming);
  const qualifies = biome.kind === roamingKind;

  if (!qualifies) {
    return { roaming, roamingKind, qualifies: false, encounter: null };
  }

  // A separate, per-expedition stream (distinct salt) so the legendary roll never
  // perturbs the route's own RNG, and re-entering the same day is harmless.
  const rng = makeRng(hash(worldSeed, `legendary-roll|${inGameDate}|${nodeId}`));
  const surfaced = rng.float() < legendarySpawnChance(inGameDate);
  if (!surfaced) {
    return { roaming, roamingKind, qualifies: true, encounter: null };
  }

  const level = legendaryLevel(roaming);
  const encounter: GeneratedEncounter = {
    speciesId: roaming.id,
    level,
    // Surfaces as a quiet, stationary presence — the classic "static" legendary slot.
    slot: 'static',
    // Shinies stay on the (unchanged) wild-instance path; the hush is the legendary
    // itself, so the overlay never forces a shiny here.
    shiny: false,
  };

  return { roaming, roamingKind, qualifies: true, encounter };
}
